import { Avatar } from '../../tasks/linear/Avatar';
import { useMemo, useState } from 'react';
import {
  CalendarClock,
  Check,
  Mail,
  Phone,
  Plus,
  Presentation,
  Trash2,
  Users,
  FileText,
  CheckSquare,
  CornerUpRight,
  Circle,
} from 'lucide-react';
import type { LeadAction, LeadActionType } from '../../../lib/leadsApi';
import {
  useCreateLeadAction,
  useDeleteLeadAction,
  useLeadActions,
  useUpdateLeadAction,
} from '../../../hooks/useLeads';
import { formatShortDate, isOverdue } from '../leadConstants';
import { Select } from '../../ui/Select';
import { DateField } from '../../ui/DateField';

/**
 * CLE-192 — Actions section for the lead detail page (spec 3.3).
 *
 * Each action shows a completion checkbox, a type icon, the title, assignee and
 * due date. Overdue (incomplete + past due) dates are red; completed actions are
 * struck-through with a completion timestamp. A "+ Add action" composer creates
 * new actions.
 */

const ACTION_TYPES: Array<{ value: LeadActionType; label: string }> = [
  { value: 'CALL', label: 'Apel' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'MEETING', label: 'Întâlnire' },
  { value: 'TASK', label: 'Sarcină' },
  { value: 'FOLLOW_UP', label: 'Follow-up' },
  { value: 'PROPOSAL', label: 'Propunere' },
  { value: 'DEMO', label: 'Demo' },
  { value: 'OTHER', label: 'Altele' },
];

function ActionIcon({ type, size = 14 }: { type: LeadActionType; size?: number }) {
  const cls = 'text-gray-500';
  switch (type) {
    case 'CALL':
      return <Phone size={size} className={cls} />;
    case 'EMAIL':
      return <Mail size={size} className={cls} />;
    case 'MEETING':
      return <Users size={size} className={cls} />;
    case 'TASK':
      return <CheckSquare size={size} className={cls} />;
    case 'FOLLOW_UP':
      return <CornerUpRight size={size} className={cls} />;
    case 'PROPOSAL':
      return <FileText size={size} className={cls} />;
    case 'DEMO':
      return <Presentation size={size} className={cls} />;
    default:
      return <Circle size={size} className={cls} />;
  }
}

export function LeadActions({ leadId }: { leadId: string }) {
  const { data: actions, isLoading } = useLeadActions(leadId);
  const update = useUpdateLeadAction(leadId);
  const remove = useDeleteLeadAction(leadId);
  const [adding, setAdding] = useState(false);

  const items = actions ?? [];

  return (
    <section className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-gray-500">Acțiuni</h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
          >
            <Plus size={13} /> Adaugă acțiune
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="text-[13px] text-gray-400">Se încarcă acțiunile…</p>
      ) : items.length === 0 && !adding ? (
        <p className="text-[13px] text-gray-400">Încă nicio acțiune.</p>
      ) : (
        <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
          {items.map((a) => (
            <ActionRow
              key={a.id}
              action={a}
              onToggle={(isCompleted) => update.mutate({ actionId: a.id, isCompleted })}
              onDelete={() => remove.mutate(a.id)}
            />
          ))}
        </ul>
      )}

      {adding && <AddActionForm leadId={leadId} onDone={() => setAdding(false)} />}
    </section>
  );
}

function ActionRow({
  action,
  onToggle,
  onDelete,
}: {
  action: LeadAction;
  onToggle: (isCompleted: boolean) => void;
  onDelete: () => void;
}) {
  const overdue = !action.isCompleted && isOverdue(action.dueDate);

  return (
    <li className="group/act flex items-center gap-2.5 px-3 py-2">
      <button
        type="button"
        onClick={() => onToggle(!action.isCompleted)}
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
          action.isCompleted
            ? 'border-brand-600 bg-brand-600 text-white'
            : 'border-gray-300 hover:border-brand-600'
        }`}
        aria-label={action.isCompleted ? 'Marchează ca neterminată' : 'Marchează ca finalizată'}
      >
        {action.isCompleted && <Check size={11} />}
      </button>

      <ActionIcon type={action.type} />

      <span
        className={`min-w-0 flex-1 truncate text-[13px] ${
          action.isCompleted ? 'text-gray-400 line-through' : 'text-gray-800'
        }`}
      >
        {action.title}
      </span>

      {action.isCompleted && action.completedAt ? (
        <span className="shrink-0 text-[11px] text-gray-400">
          Finalizat {formatShortDate(action.completedAt)}
        </span>
      ) : (
        action.dueDate && (
          <span
            className={`inline-flex shrink-0 items-center gap-0.5 text-[11px] ${
              overdue ? 'font-medium text-red-500' : 'text-gray-500'
            }`}
            title={overdue ? 'Întârziat' : 'Termen'}
          >
            <CalendarClock size={11} /> {formatShortDate(action.dueDate)}
          </span>
        )
      )}

      {action.assignee && (
        <span className="inline-flex shrink-0">
          <Avatar name={action.assignee.name} userId={action.assignee.id} size={20} />
        </span>
      )}

      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 text-gray-400 opacity-0 hover:text-red-500 group-hover/act:opacity-100"
        aria-label="Șterge acțiunea"
      >
        <Trash2 size={13} />
      </button>
    </li>
  );
}

function AddActionForm({ leadId, onDone }: { leadId: string; onDone: () => void }) {
  const create = useCreateLeadAction(leadId);
  const [type, setType] = useState<LeadActionType>('FOLLOW_UP');
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');

  const canSubmit = useMemo(() => title.trim().length > 0, [title]);

  function submit() {
    if (!canSubmit) return;
    create.mutate(
      {
        type,
        title: title.trim(),
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      },
      {
        onSuccess: () => {
          setTitle('');
          setDueDate('');
          onDone();
        },
      },
    );
  }

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-card p-2.5">
      <div className="flex items-center gap-2">
        <Select
          label="Tip acțiune"
          hideLabel
          size="sm"
          value={type}
          onChange={(v) => setType(v as LeadActionType)}
          options={ACTION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
          className="w-32 shrink-0"
        />
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            }
            if (e.key === 'Escape') onDone();
          }}
          placeholder="Titlu acțiune…"
          className="min-w-0 flex-1 rounded border border-gray-200 bg-transparent px-2 py-1 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-brand-600 focus:outline-none"
        />
        <DateField value={dueDate} onChange={setDueDate} className="w-40 shrink-0" />
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded px-2 py-1 text-[12px] text-gray-500 hover:bg-gray-200"
        >
          Anulează
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit || create.isPending}
          className="rounded bg-brand-600 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-40"
        >
          {create.isPending ? 'Se adaugă…' : 'Adaugă'}
        </button>
      </div>
    </div>
  );
}
